import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { formatPhone } from '@jupi/shared'
import { formatDateTime, formatHours, formatRange } from '@jupi/ui'
import type { Booking } from '../api/queries'
import { bookingIsAog, defectsLink, useAircraftStatuses } from '../features/calendar/aircraftStatus'
import { BOOKING_TYPE_ICONS, typeOf } from '../features/calendar/bookingType'
import { standbyPlaceLabel } from '../features/calendar/standbyPlace'

// One booking as a card. This is the primary way bookings are read on a
// phone -- the legacy tables have eight columns and are unusable below
// about 900px.
export default function BookingCard({
  booking,
  onClick,
  showAircraft = true,
}: {
  booking: Booking
  onClick?: () => void
  showAircraft?: boolean
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const status = useAircraftStatuses().get(booking.registration)
  // On AOG days the booking stays but is marked AOG; a restricted aircraft
  // keeps it with a warning that opens the defect.
  const onHold = bookingIsAog(booking, status)
  const restricted = status?.light === 'restricted'
  const isOption = booking.kind === 'option'
  const accent = onHold ? 'error.main' : booking.isMaintenance ? 'grey.500' : isOption ? 'warning.main' : 'primary.main'

  const content = (
    <Box sx={{ display: 'flex', width: '100%' }}>
      {/* A colour bar rather than a coloured background: it survives dark
          mode and does not fight the text contrast. */}
      <Box sx={{ width: 4, bgcolor: accent, flexShrink: 0 }} />
      <Box sx={{ p: 1.5, flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {formatRange(booking.startsAt, booking.endsAt)}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {showAircraft ? `${booking.registration} · ` : ''}
              {booking.displayName}
              {booking.phone ? ` · ${formatPhone(booking.phone)}` : ''}
              {booking.instructorName ? ` + ${booking.instructorName}` : ''}
              {booking.lessonNumber ? ` · ${booking.lessonNumber}` : ''}
              {booking.comment ? ` · ${booking.comment}` : ''}
            </Typography>
          </Box>
          {onHold ? (
            <Chip size="small" color="error" icon={<Icon icon="mdi:hand-front-left" />} label={t('aircraftDefect.airworthiness.aog')} />
          ) : restricted ? (
            <Chip
              size="small"
              color="warning"
              clickable
              icon={<Icon icon="mdi:alert-outline" />}
              label={t('aircraftDefect.airworthiness.restricted')}
              onClick={(e) => {
                e.stopPropagation()
                navigate(defectsLink(booking.registration))
              }}
            />
          ) : null}
          <Chip
            size="small"
            icon={<Icon icon={BOOKING_TYPE_ICONS[typeOf(booking)]} />}
            label={
              booking.isMaintenance
                ? t('booking.maintenance')
                : booking.isFirePatrol
                  ? t('firePatrol.chip')
                  : isOption
                  ? t('booking.option')
                  : t('booking.reservation')
            }
            color={booking.isMaintenance ? 'default' : isOption ? 'warning' : 'primary'}
            variant={isOption ? 'outlined' : 'filled'}
          />
        </Stack>
        {isOption && booking.queuePosition ? (
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, alignItems: 'center' }}>
            <Icon icon="mdi:format-list-numbered" width={14} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {standbyPlaceLabel(t, booking.queuePosition)}
            </Typography>
          </Stack>
        ) : null}
        {isOption && booking.expiresAt ? (
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, alignItems: 'center' }}>
            <Icon icon="mdi:timer-sand" width={14} />
            <Typography variant="caption" color="text.secondary">
              {t('booking.standby.dropsAt', { when: formatDateTime(booking.expiresAt) })}
            </Typography>
          </Stack>
        ) : null}
        {booking.estimatedFlightHours > 0 ? (
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, alignItems: 'center' }}>
            <Icon icon="mdi:clock-outline" width={14} />
            <Typography variant="caption" color="text.secondary">
              {formatHours(booking.estimatedFlightHours)} h
            </Typography>
          </Stack>
        ) : null}
      </Box>
    </Box>
  )

  return (
    <Card variant="outlined" sx={{ overflow: 'hidden', opacity: onHold ? 0.75 : 1 }}>
      {onClick ? <CardActionArea onClick={onClick}>{content}</CardActionArea> : content}
    </Card>
  )
}
